import { useCallback, useContext, type FC } from "react";
import { useNavigate } from "react-router-dom";

import { Button } from "@/components/flexkit/Button";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/podkit/dialog/Dialog";
import { CreditsExhaustionContext } from "@/components/QueryClientProvider";
import { useMembership } from "@/hooks/use-membership";
import { TrackLocations } from "@/hooks/use-segment";
import { useOrganization } from "@/queries/organization-queries";
import { OrganizationRole, OrganizationTier } from "gitpod-next-api/gitpod/v1/organization_pb";

export const CreditsExhaustionModal: FC = () => {
    const { isCreditsExhausted, setCreditsExhaustion, onCloseCallback, clearCallback } =
        useContext(CreditsExhaustionContext);

    const navigate = useNavigate();
    const { data: organization, isPending: isPendingOrganization } = useOrganization();
    const { membership, isPending: isPendingMembership } = useMembership();

    const isAdmin = membership?.userRole === OrganizationRole.ADMIN;
    const isOnCoreTier = organization?.tier === OrganizationTier.CORE;
    const isPending = isPendingOrganization || isPendingMembership;

    const handleVisibilityChange = useCallback(
        (open: boolean) => {
            if (!open) {
                setCreditsExhaustion(false);
                if (onCloseCallback) {
                    onCloseCallback();
                    clearCallback();
                }
            }
        },
        [setCreditsExhaustion, onCloseCallback, clearCallback],
    );

    const handleViewBilling = useCallback(() => {
        navigate("/settings/billing");
        handleVisibilityChange(false);
    }, [handleVisibilityChange, navigate]);

    const handleUpgrade = useCallback(() => {
        navigate("/settings/billing?change-plan=true");
        handleVisibilityChange(false);
    }, [handleVisibilityChange, navigate]);

    return (
        <Dialog open={isCreditsExhausted && !isPending} onOpenChange={handleVisibilityChange}>
            <DialogContent className="max-w-md" data-track-location={TrackLocations.CreditsExhaustionModal}>
                <DialogHeader className="mb-1.5">
                    <DialogTitle>You&apos;ve used all your credits</DialogTitle>
                </DialogHeader>

                {isAdmin && isOnCoreTier && (
                    <>
                        <DialogDescription>
                            Add more credits to keep your environments running and continue building.
                        </DialogDescription>
                        <DialogFooter className="justify-end">
                            <Button
                                variant="primary"
                                onClick={handleViewBilling}
                                data-tracking-id="get-credits-credit-exhaustion-modal"
                            >
                                Get more credits
                            </Button>
                        </DialogFooter>
                    </>
                )}

                {isAdmin && !isOnCoreTier && (
                    <>
                        <DialogDescription>Upgrade to Core to add more credits and keep building.</DialogDescription>
                        <DialogFooter className="justify-end">
                            <Button
                                variant="outline"
                                onClick={handleViewBilling}
                                data-tracking-id="recovery-credits-credit-exhaustion-modal"
                            >
                                Save my work first
                            </Button>
                            <Button
                                variant="primary"
                                onClick={handleUpgrade}
                                data-tracking-id="upgrade-credit-exhaustion-modal"
                            >
                                Upgrade to Core
                            </Button>
                        </DialogFooter>
                    </>
                )}

                {!isAdmin && (
                    <>
                        <DialogDescription>
                            {isOnCoreTier
                                ? "Your organization has run out of credits. Ask your admin to add more credits so you can continue working."
                                : "Your organization has used all its free credits. Ask your admin to upgrade so your team can keep building."}
                        </DialogDescription>
                        <DialogFooter className="justify-end">
                            <Button
                                variant="outline"
                                onClick={() => handleVisibilityChange(false)}
                                data-tracking-id="ok-credit-exhaustion-modal"
                            >
                                Ok
                            </Button>
                        </DialogFooter>
                    </>
                )}
            </DialogContent>
        </Dialog>
    );
};
